import { useState } from 'react';

export default function LeadInventoryPagination({ 
    page, 
    setPage, 
    pageSize, 
    setPageSize, 
    totalLeads, 
    absoluteTotal, 
    totalPages 
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    return (
        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center border-t border-gray-200 p-3 md:p-4 gap-3">
            {/* Info text - simplified on mobile */}
            <div className="flex flex-col md:flex-row gap-1 md:gap-2 text-xs md:text-sm">
                <span className="text-gray-600">
                    {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalLeads)} of {totalLeads}
                    <span className="hidden md:inline"> leads visible</span>
                </span>
                <span className="text-gray-500 text-xs md:text-sm">
                    (total: {absoluteTotal})
                </span>
            </div>
            {/* Controls */}
            <div className="flex gap-2 items-center justify-between md:justify-end">
                {/* Records per page - abbreviated on mobile */}
                <span className="text-xs md:text-sm flex items-center whitespace-nowrap">
                    <span className="hidden md:inline">Records per page</span>
                    <span className="md:hidden">Per page</span>
                </span>
                <div className="relative">
                    <button
                        onClick={() => setIsPageSizeOpen(!isPageSizeOpen)}
                        className="min-w-[44px] min-h-[44px] md:min-h-0 px-3 py-2 bg-white border-b border-gray-800 focus:outline-none hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center gap-1"
                    >
                        <span className="text-sm md:text-base">{pageSize}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isPageSizeOpen && (
                        <div className="absolute z-10 mt-1 right-0 w-auto bg-white border border-gray-300 rounded-lg shadow-lg">
                            {[10, 15, 20, 25, 30, 35, 40, 45, 50].map((size) => (
                                <button
                                    key={size}
                                    onClick={() => {
                                        setPageSize(size);
                                        setIsPageSizeOpen(false);
                                        setPage(1);
                                    }}
                                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                                        pageSize === size ? 'bg-blue-50 text-blue-600' : ''
                                    }`}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Previous button */}
                <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="min-w-[44px] min-h-[44px] md:min-h-0 px-3 py-2 text-sm font-medium text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 active:bg-gray-200 rounded-lg"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
                    </svg>
                </button>
                
                {/* Page selector */}
                <div className="relative">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="min-w-[44px] min-h-[44px] md:min-h-0 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none hover:bg-gray-50 active:bg-gray-100 flex items-center gap-1"
                    >
                        <span className="text-xs md:text-sm whitespace-nowrap">
                            <span className="hidden md:inline">Page </span>{page}
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isOpen && (
                        <div className="absolute z-10 mt-1 right-0 w-32 md:w-36 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index + 1}
                                    onClick={() => {
                                        handlePageChange(index + 1);
                                        setIsOpen(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                                        page === index + 1 ? 'bg-blue-50 text-blue-600' : ''
                                    }`}
                                >
                                    Page {index + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Next button */}
                <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="min-w-[44px] min-h-[44px] md:min-h-0 px-3 py-2 text-sm font-medium text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 active:bg-gray-200 rounded-lg"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
                    </svg>                            
                </button>
            </div>
        </div>
    );
}